import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useRenderTemplate } from '#/entities/template'
import type { useCreateToolbarItem } from '#/features/shipment-toolbar'
import { useGetMeApiV1UsersMeGet } from '#/shared/api/generated/auth/theCargoAuth'
import { useGetShipmentApiV1ShipmentsShipmentIdGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { isValidEmail } from '#/shared/lib/format'
import { expandTemplateChips, toEmailBodyHtml } from '#/shared/lib/html'
import { toast } from '#/shared/ui/sonner'

import { trimNonEmpty } from './helpers'
import { useOrderFiles } from './file-slice'
import { useResetOnShipmentChange, useSeededList } from './reseed'

function useEmailContext(shipmentId: string | undefined, armed: boolean) {
  const meQuery = useGetMeApiV1UsersMeGet({ query: { enabled: armed } })
  const shipmentQuery = useGetShipmentApiV1ShipmentsShipmentIdGet(
    shipmentId ?? '',
    { query: { enabled: armed && Boolean(shipmentId) } },
  )
  const customer = shipmentQuery.data?.data.customer
  return {
    fromEmail: meQuery.data?.data.email ?? '',
    customerEmail: customer?.email ?? '',
  }
}

function cleanEmailList(values: string[]): string[] {
  return values.map((v) => v.trim()).filter((v) => v.length > 0)
}

function nonEmptyList(values: string[]): string[] | undefined {
  const cleaned = cleanEmailList(values)
  return cleaned.length > 0 ? cleaned : undefined
}

interface EmailDraft {
  to: string[]
  subject: string
  body: string
  cc: string[]
  bcc: string[]
  attachedFileIds: string[]
}

function buildEmailPayload(draft: EmailDraft, ctx: { fromEmail: string }) {
  return {
    to: draft.to,
    cc: nonEmptyList(draft.cc),
    bcc: nonEmptyList(draft.bcc),
    subject: trimNonEmpty(draft.subject),
    body: trimNonEmpty(draft.body),
    fromEmail: trimNonEmpty(ctx.fromEmail),
    attachedFileIds:
      draft.attachedFileIds.length > 0 ? draft.attachedFileIds : undefined,
  }
}

function useEmailRecipients(seedEmail: string, shipmentId: string | undefined) {
  // Re-seeds the recipient from the customer on every row switch (the panel
  // stays mounted); manual edits within a row survive until the next switch.
  const [toEmails, setToEmails] = useSeededList(shipmentId, seedEmail)

  const addEmail = (typed: string) => {
    const trimmed = typed.trim()
    if (!isValidEmail(trimmed)) return
    setToEmails((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]))
  }

  const removeEmail = (id: string) => {
    setToEmails((prev) => prev.filter((value) => value !== id))
  }

  const reset = () => setToEmails(seedEmail ? [seedEmail] : [])

  return { toEmails, addEmail, removeEmail, reset }
}

function useEmailAttachments(
  shipmentId: string | undefined,
  uploadFile: ReturnType<typeof useCreateToolbarItem>['uploadFile'],
) {
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(
    () => new Set(),
  )

  // Clear attachments when the panel switches to another row (it never
  // remounts), so one shipment's files don't ride along to the next.
  useResetOnShipmentChange(shipmentId, () => setSelectedFileIds(new Set()))

  const toggleFile = (id: string, selected: boolean) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev)
      if (selected) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const uploadAttachment = async (files: FileList) => {
    if (!shipmentId) return
    for (const file of Array.from(files)) {
      try {
        await uploadFile({ file, isOrderFile: true })
      } catch {
        return
      }
    }
  }

  const reset = () => setSelectedFileIds(new Set())

  return { selectedFileIds, toggleFile, uploadAttachment, reset }
}

interface EmailSliceDeps {
  shipmentId: string | undefined
  armed: boolean
  createEmail: ReturnType<typeof useCreateToolbarItem>['createEmail']
  uploadFile: ReturnType<typeof useCreateToolbarItem>['uploadFile']
  isSending: boolean
  isUploading: boolean
  closeTab: () => void
}

/** Resolve a template against the shipment and load it into the composer.
 *  The render endpoint returns chips already carrying resolved values — with
 *  a shipment context the composer shows those values as plain text, not
 *  chips (chips are for token-only contexts: mass jobs, template editing). */
function useApplyTemplate(
  shipmentId: string | undefined,
  setBody: (body: string) => void,
  setSubject: (subject: string) => void,
) {
  const { t } = useTranslation()
  const renderTemplate = useRenderTemplate()
  return async (templateId: string, insertValues?: Record<string, string>) => {
    if (!shipmentId) return
    let rendered
    try {
      rendered = await renderTemplate(templateId, shipmentId)
    } catch (error) {
      // The UI fires this handler without awaiting it, so an escaped rejection
      // would surface only as an unhandled-promise Sentry error.
      toast.error(
        getApiErrorDetail(error) ?? t('shipmentToolbar.create.templateError'),
      )
      return
    }
    setBody(expandTemplateChips(rendered.body, insertValues))
    if (rendered.subject) setSubject(rendered.subject)
  }
}

function useEmailDraft(shipmentId: string | undefined) {
  const [body, setBody] = useState('')
  const [subject, setSubject] = useState('')
  const [cc, setCc] = useState<string[]>([])
  const [bcc, setBcc] = useState<string[]>([])

  const clear = () => {
    setBody('')
    setSubject('')
    setCc([])
    setBcc([])
  }

  // Drop the composed draft when the panel switches to another row (recipients
  // and attachments re-seed themselves); it never remounts, so a message typed
  // for one shipment must not linger on the next.
  useResetOnShipmentChange(shipmentId, clear)

  return { body, setBody, subject, setSubject, cc, setCc, bcc, setBcc, clear }
}

export function useEmailSlice({
  shipmentId,
  armed,
  createEmail,
  uploadFile,
  isSending,
  isUploading,
  closeTab,
}: EmailSliceDeps) {
  const { body, setBody, subject, setSubject, cc, setCc, bcc, setBcc, clear } =
    useEmailDraft(shipmentId)
  const ctx = useEmailContext(shipmentId, armed)
  const { files: orderFiles, isLoading: orderFilesLoading } = useOrderFiles(
    shipmentId,
    armed,
  )
  const recipients = useEmailRecipients(ctx.customerEmail, shipmentId)
  const attachments = useEmailAttachments(shipmentId, uploadFile)
  const applyTemplate = useApplyTemplate(shipmentId, setBody, setSubject)

  const reset = () => {
    clear()
    recipients.reset()
    attachments.reset()
    closeTab()
  }

  const onSend = async (html: string, insertValues: Record<string, string>) => {
    if (!shipmentId || isSending || recipients.toEmails.length === 0) return
    if (!subject.trim() && !html.trim()) return
    const payload = buildEmailPayload(
      {
        to: recipients.toEmails,
        subject,
        // The direct-send endpoint doesn't render templates — resolve
        // chips to their shipment-context values here.
        body: toEmailBodyHtml(expandTemplateChips(html, insertValues)),
        cc,
        bcc,
        attachedFileIds: Array.from(attachments.selectedFileIds),
      },
      ctx,
    )
    try {
      await createEmail(payload)
    } catch {
      return // onError already surfaced the failure; keep the draft
    }
    reset()
  }

  return {
    value: body,
    onChange: setBody,
    subject,
    onSubjectChange: setSubject,
    onCcChange: setCc,
    onBccChange: setBcc,
    applyTemplate,
    onCancel: reset,
    onSend,
    isSending,
    fromEmail: ctx.fromEmail,
    toEmails: recipients.toEmails,
    addEmail: recipients.addEmail,
    removeEmail: recipients.removeEmail,
    canSend: Boolean(shipmentId && recipients.toEmails.length > 0),
    orderFiles,
    orderFilesLoading,
    selectedFileIds: attachments.selectedFileIds,
    onToggleFile: attachments.toggleFile,
    onUploadAttachment: attachments.uploadAttachment,
    isUploading,
  }
}
